



























































































































































































































import Vue from 'vue';
import { ALForm } from '@/common/component.types';
import { AxiosError } from 'axios';

import { IItem, IItemTag, ITag, Item, ItemFieldError, ItemTag } from '@/models';
import { mapActions, mapGetters } from 'vuex';
import { ErrorManager } from '@/models/error';
import { cloneDeep } from 'lodash';
import { diffDeep, getPropertyFromItem } from '@/utils/helpers';

import CountryPicker from '@/components/common/CountryPicker.vue';
import ItemTagPicker from '../item/ItemTagPicker.vue';
import AlDialog from '../core/Dialog/ALDialog.vue';
import ErrorAlert from '../common/ErrorAlert.vue';

export interface Options extends InstanceType<typeof Vue> {
  $refs: {
    form: ALForm;
  };
}

export default Vue.extend<Options>({
  functional: false
}).extend({
  name: 'ItemFormDialog',

  components: {
    CountryPicker,
    ItemTagPicker,
    AlDialog,
    ErrorAlert
  },

  data: () => ({
    isFormValid: false,
    dialog: false,
    isEdit: false,
    item: null as null | IItem,
    loading: false,
    errorMessage: '',
    fieldError: new ItemFieldError(),
    detailErrorMessage: '',
    abcCodeOptions: ['C'],
    unitOptions: [
      { code: 'BAG', description: 'Bag' },
      { code: 'BX', description: 'Box' },
      { code: 'CA', description: 'Case' },
      { code: 'CMT', description: 'Hundred Meters' },
      { code: 'COL', description: 'Coil' },
      { code: 'EA', description: 'Each' },
      { code: 'FT', description: 'Foot' },
      { code: 'FT2', description: 'Square Feet' },
      { code: 'GA', description: 'Gallons' },
      { code: 'IN', description: 'Inch' },
      { code: 'KG', description: 'Kilogram' },
      { code: 'LB', description: 'Pound' },
      { code: 'M', description: 'Meter' },
      { code: 'M2', description: 'Square Meter' },
      { code: 'MM', description: 'Millimeter' },
      { code: 'OZ', description: 'Ounces' },
      { code: 'PKG', description: 'Package' },
      { code: 'REL', description: 'Reel' },
      { code: 'RL1', description: 'Roll' },
      { code: 'Rolls', description: 'Box' },
      { code: 'SH', description: 'Sheet' },
      { code: 'WKS', description: 'Weeks' }
    ],
    itemOriginalCopy: null as null | IItem,

    requiredRules: [(v: string) => !!v || 'This field is required']
  }),

  methods: {
    ...mapActions({
      createItem: 'item/createItem',
      updateItem: 'item/updateItem'
    }),

    resetFormValidation(): void {
      if (this.$refs.form) {
        return this.$refs.form.resetValidation();
      }
    },

    validateForm(): boolean {
      let valid = false;
      if (this.$refs.form) {
        valid = this.$refs.form.validate();
      }
      return valid;
    },

    open(item = new Item(), isEdit = false) {
      this.itemOriginalCopy = item;
      this.item = cloneDeep(item);
      this.isEdit = isEdit;
      this.clearAllErrors();
      this.dialog = true;

      this.resetFormValidation();
    },

    close() {
      this.dialog = false;
    },

    clearAllErrors(): void {
      this.fieldError.clearErrors();
      this.errorMessage = '';
      this.detailErrorMessage = '';
    },

    async saveItem() {
      if (this.validateForm()) {
        this.loading = true;
        this.clearAllErrors();

        try {
          if (this.isEdit) {
            const itemDelta = diffDeep(this.item, this.itemOriginalCopy, true);
            itemDelta!.item = this.item!.item;

            await this.updateItem(itemDelta);
          } else {
            await this.createItem(this.item);
          }
          this.close();
        } catch (err) {
          const error = err as AxiosError;
          this.errorMessage = ErrorManager.extractApiError(error);
          if (error.response) {
            // client received an error response that falls out of range 2xx
            if (ItemFieldError.isItemFieldError(error.response.data)) {
              this.fieldError = new ItemFieldError(error.response.data);
            } else if (error.response.data.detail) {
              this.detailErrorMessage = error.response.data.detail;
            }
          }
        } finally {
          this.loading = false;
        }
      }
    },

    selectTag(tag: number) {
      // tag may be a number or an object depending if the value is coming from the server or locally
      const tagIndex = this.item?.tags.findIndex(
        itemTag => tag === this.getTagValue(itemTag.tag)
      );

      // if tag not currently in tag list as to not add duplicates
      if (tagIndex === -1) {
        // display tag visually in
        const itemTag = new ItemTag({
          tag: tag
        });
        this.item?.tags.push(itemTag);
      }
    },

    removeTag(tag: ItemTag) {
      // tag may be a number or an object depending if the value is coming from the server or locally
      const tagIndex = this.item?.tags.findIndex(
        itemTag => this.getTagValue(tag.tag) === this.getTagValue(itemTag.tag)
      );
      // tag can be index of `0`
      if (tagIndex != null && tagIndex > -1) {
        this.item?.tags.splice(tagIndex, 1);
      }
    },

    getTagValue(tag: ITag | IItemTag | number): number {
      return getPropertyFromItem(tag as IItemTag, 'id', tag);
    }
  },

  computed: {
    ...mapGetters({
      tags: 'itemTag/getTags',
      getTagById: 'itemTag/getTagById',
      getItemById: 'item/getItemById'
    }),

    computedItemTags(): IItemTag[] {
      return (
        this.item?.tags.map(itemTag => {
          return new ItemTag({
            id: itemTag.id,
            tag:
              typeof itemTag.tag === 'number'
                ? this.getTagById(this.getTagValue(itemTag.tag))
                : itemTag.tag
          });
        }) ?? []
      );
    }
  }
});
